import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { getAvatar } from "../services/MSGraphApiCall";
import { loginRequest } from "../../authConfig";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Button from "react-bootstrap/Button";
import NavDropdown from "react-bootstrap/NavDropdown";
import logo from "../../assets/logo.png";
import oktaIcon from "../../assets/okta.png";

const NavBar = ({ token, renewToken }) => {
  const [timeLeft, setTimeLeft] = useState();
  const [avatar, setAvatar] = useState(null);
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const activeAccount = instance.getActiveAccount();
  let isAuthenticated = useIsAuthenticated();
  const timeout = 10;
  const soarOrg =
    process.env.REACT_APP_ENVIRONMENT === "Production" ? "ccplive" : "d365base";
  const [modules, setModules] = useState([]);
  const [safyInfo, setSafyinfo] = useState({});
  const [isAuthenticatedSAFY, setIsAuthenticatedSAFY] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // console.log("Navbar Authentication", isAuthenticated, isAuthenticatedSAFY);
    if (isAuthenticated) {
      const checkAuthentication = async () => {
        const safyInfo = localStorage.getItem("safyInfo");
        if (safyInfo?.length > 0) {
          setIsAuthenticatedSAFY(true);
          setSafyinfo(JSON.parse(safyInfo));
          // console.log("Authenticated", isAuthenticatedSAFY);
        } else {
          console.log("Refreshing");
          setTimeout(() => {
            setIsAuthenticatedSAFY(false);
            checkAuthentication();
          }, 1000);
        }
      };

      checkAuthentication();
    }
  }, [isAuthenticated, isAuthenticatedSAFY]);

  useEffect(() => {
    setModules(
      JSON.parse(localStorage.getItem("safyInfo"))?.user?.modules || []
    );
  }, [safyInfo]);
  //On url path change, check if token is about to expire and refresh if needed
  //activeAccount.idTokenClaims
  useEffect(() => {
    if (!instance.getActiveAccount()?.idTokenClaims) {
      return;
    }

    if (timeLeft?.difference < 3600) {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: instance.getActiveAccount(),
        })
        .then(() => {
          renewToken();
          console.log("navigation: token refreshed");
        })
        .catch((e) => {
          instance.acquireTokenPopup(loginRequest).then(() => {
            console.log("navigation: token refreshed");
          });
        });
    }

    // Purpposely left out dependencies to avoid infinite loop (instance, timeleft, token, activeAccount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  //Get Avatar
  useEffect(() => {
    if (avatar || !instance.getActiveAccount() || !isAuthenticated) {
      return;
    }

    const loadAvatar = async () => {
      const response = await getAvatar();
      setAvatar(window.URL.createObjectURL(response));
    };

    loadAvatar();
  }, [avatar, isAuthenticated]);

  //calculate time left from MSAL instance
  const calculateTimeLeft = useCallback(() => {
    if (!instance.getActiveAccount()?.idTokenClaims) {
      return;
    }

    let difference =
      instance.getActiveAccount().idTokenClaims.exp * 1000 - Date.now();

    setTimeLeft({
      hours: ("0" + Math.floor((difference / (1000 * 60 * 60)) % 24)).slice(-2),
      minutes: ("0" + Math.floor((difference / 1000 / 60) % 60)).slice(-2),
      seconds: ("0" + Math.floor((difference / 1000) % 60)).slice(-2),
      difference: Math.floor(difference / 1000),
    });

    if (difference < timeout * 60000 && difference > timeout * 60000 - 1000) {
      toast.error(
        <div>
          Session Ending soon
          <div>
            <button className="btn btn-light" onClick={() => renewToken()}>
              Refresh
            </button>
          </div>
        </div>,
        { autoClose: timeout * 60000, pauseOnFocusLoss: false }
      );
    }

    if (isAuthenticated && difference < 0) {
      console.log("login timed out");
      instance.logoutRedirect({
        onRedirectNavigate: (url) => {
          console.log("logging out");
          navigate("/");
          return false;
        },
      });
      navigate("/");
    }
  }, [token, instance, navigate, isAuthenticated, location.pathname]);

  // const setInfo = useMemo(() => {
  //   if (safyInfo) {
  //     calculateTimeLeft();
  //   }
  // }, [safyInfo, calculateTimeLeft]);

  //call calculateTimeLeft every second
  // useEffect(() => {
  //   calculateTimeLeft();
  //   const timer = setTimeout(() => {
  //     calculateTimeLeft();
  //   }, 1000);
  //   return () => clearTimeout(timer);
  // }, [calculateTimeLeft]);

  useEffect(() => {
    const timer =
      timeLeft !== null
        ? setTimeout(() => {
            calculateTimeLeft();
          }, 510)
        : null;
    return () => {
      clearTimeout(timer);
    };
  }, [timeLeft, calculateTimeLeft]);

  const advancedFindLink = () => {
    let pathname = "";
    if (location.pathname !== "")
      pathname = location.pathname.split("/")[1].split("?")[0];

    let advancedFindUrl = "";

    switch (pathname.toLowerCase()) {
      case "mycaseload":
      case "allcaseloads":
      case "reports":
      case "intake":
      case "client":
      case "contact":
        advancedFindUrl = `https://${soarOrg}.safy.org/main.aspx?extraqs=%3fDataProvider%3dMicrosoft.Crm.Application.Platform.Grid.GridDataProviderQueryBuilder%26EntityCode%3d2%26UIProvider%3dMicrosoft.Crm.Application.Controls.GridUIProvider&pagetype=advancedfind`;
        break;
      case "fosterhomes":
        advancedFindUrl = `https://${soarOrg}.safy.org/main.aspx?extraqs=%3fDataProvider%3dMicrosoft.Crm.Application.Platform.Grid.GridDataProviderQueryBuilder%26EntityCode%3d10221%26QueryId%3d%257b0DD425FF-F132-4491-B929-67DA56065E02%257d%26UIProvider%3dMicrosoft.Crm.Application.Controls.GridUIProvider%26ViewType%3d1039&pagetype=advancedfind`;
        break;
      default:
        break;
    }

    if (advancedFindUrl !== "")
      return (
        <div className="me-4">
          <i
            className="fa fa-binoculars fa-lg mt-4 text-white"
            onClick={() => {
              window.open(advancedFindUrl, "_blank");
            }}
          ></i>
        </div>
      );
    else return null;
  };

  const unreadCount = localStorage.getItem("unreadMessagesCount") || 0;

  const handleNavClick = () => {
    setExpanded(false);
  };

  //Navigation Bar
  return (
    <>
      {isAuthenticated && (
        <>
          <Navbar
            className={
              process.env.REACT_APP_ENVIRONMENT !== "Production"
                ? "bg-safy3-1 px-4"
                : "bg-safy1-1 px-4"
            }
            collapseOnSelect
            variant="dark"
            expand={`${modules.includes("Admin") ? "xxl" : "lg"}`}
            expanded={expanded}
            onToggle={(expanded) => setExpanded(expanded)}
          >
            <img
              src={"data:image/png " + logo}
              alt="logo"
              className="me-4"
              style={{ width: "48px" }}
            ></img>
            <Navbar.Brand href="/">{process.env.REACT_APP_NAME}</Navbar.Brand>
            <Navbar.Toggle aria-controls="navbarNavAltMarkupv" />
            <Navbar.Collapse id="navbarNavAltMarkup">
              <Nav className="me-auto">
                <NavLink
                  className="nav-item nav-link"
                  to="/"
                  id="home"
                  onClick={handleNavClick}
                >
                  <div className="position-relative">
                    Home
                    {unreadCount > 0 ? (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill text-bg-danger">
                        {unreadCount}
                        <span className="visually-hidden">unread messages</span>
                      </span>
                    ) : null}
                  </div>
                </NavLink>
                {modules.includes("Intake") === true && (
                  <NavLink
                    className="nav-item nav-link"
                    to="/intake"
                    id="intake"
                    onClick={handleNavClick}
                  >
                    Intake
                  </NavLink>
                )}
                {modules.includes("Mobile Crisis") === true && (
                  <NavLink
                    className="nav-item nav-link"
                    to="/crisis"
                    id="crisis"
                    onClick={handleNavClick}
                  >
                    Crisis Referrals
                  </NavLink>
                )}
                {modules.includes("My Caseload") === true && (
                  <NavLink
                    className="nav-item nav-link"
                    to="/mycaseload"
                    id="mycaseload"
                    onClick={handleNavClick}
                  >
                    My Caseload
                  </NavLink>
                )}
                {modules.includes("Team Caseload") === true && (
                  <NavLink
                    className="nav-item nav-link"
                    to="/allcaseloads"
                    id="allcaseloads"
                    onClick={handleNavClick}
                  >
                    Team Caseload
                  </NavLink>
                )}
                {modules.includes("Foster Homes") === true &&
                  modules.includes("Admin") === false && (
                    <NavLink
                      className="nav-item nav-link"
                      to="/fosterhomes"
                      id="fosterhomes"
                      onClick={handleNavClick}
                    >
                      Foster Homes
                    </NavLink>
                  )}
                {(modules.includes("SAFY Homes") === true ||
                  modules.includes("Admin")) && (
                  <NavLink
                    className="nav-item nav-link"
                    to="/pipeline"
                    id="pipeline"
                    onClick={handleNavClick}
                  >
                    Pipeline
                  </NavLink>
                )}
                <NavLink
                  className="nav-item nav-link"
                  to="/approvals"
                  id="approvals"
                  onClick={handleNavClick}
                >
                  Approvals
                </NavLink>
                {modules.includes("Reports") === true && (
                  <NavLink
                    className="nav-item nav-link"
                    to="/reports"
                    id="reports"
                    onClick={handleNavClick}
                  >
                    Reports
                  </NavLink>
                )}
                <NavLink
                  className="nav-item nav-link"
                  to={
                    process.env.REACT_APP_ENVIRONMENT !== "Production" &&
                    modules.includes("Admin")
                      ? "supervision2"
                      : new Date() > new Date("2024-10-6")
                      ? "supervision2"
                      : "supervision"
                  }
                  id="supervision"
                  onClick={handleNavClick}
                >
                  Supervision
                </NavLink>
                {modules.includes("Admin") ||
                process.env.REACT_APP_ENVIRONMENT === "Production" ? (
                  <NavLink
                    className="nav-item nav-link"
                    to="/oncall"
                    id="oncall"
                    onClick={handleNavClick}
                  >
                    On Call
                  </NavLink>
                ) : null}
                {(modules.includes("Rev Cycle") ||
                  modules.includes("Admin")) && (
                  <NavDropdown
                    className="RevDropdown"
                    title="Rev Cycle"
                    id="rev-dropdown"
                  >
                    <NavDropdown.Item onClick={() => navigate("/rev/payors")}>
                      Payors
                    </NavDropdown.Item>
                    <NavDropdown.Item onClick={() => navigate("/rev/rules")}>
                      Census Billing Rules
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={() => navigate("/rev/client/rules")}
                    >
                      Client Specific Billing Rules
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={() => navigate("/residence/fosterhomes")}
                    >
                      Census Foster Homes
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={() =>
                        navigate("/residence/programs/independent living")
                      }
                    >
                      Independent Living
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={() =>
                        navigate("/residence/programs/family preservation")
                      }
                    >
                      Family Preservation
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={() => navigate("/rev/census/services")}
                    >
                      Census
                    </NavDropdown.Item>
                    {/* <NavDropdown.Item
                                      onClick={() => navigate("/rev/services")}
                                    >
                                      Billable Services
                                    </NavDropdown.Item> */}
                  </NavDropdown>
                )}
                {modules.includes("Admin") && (
                  <>
                    <NavDropdown
                      className="AdminDropdown"
                      title="Admin"
                      id="admin-dropdown"
                    >
                      <NavDropdown.Item onClick={() => navigate("/logins")}>
                        Login History
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => navigate("/errors")}>
                        Error Logs
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        onClick={() => navigate("/binticompare")}
                      >
                        Binti Compare
                      </NavDropdown.Item>
                      {process.env.REACT_APP_ENVIRONMENT === "Production" ? (
                        <NavDropdown.Item
                          onClick={() =>
                            navigate(
                              "/SAFYFPPortalCMS/3C41E994-D76E-EF11-991A-9B83D80529EF/AD6610F3-916F-EF11-991A-9B83D80529EF"
                            )
                          }
                        >
                          FP Portal Resources
                        </NavDropdown.Item>
                      ) : null}
                      {/* <NavDropdown.Item onClick={() => navigate("/aim")}>
                        AI Model
                      </NavDropdown.Item> */}
                      <NavDropdown.Divider />
                      <NavDropdown.Item
                        onClick={() => navigate("/admin/services")}
                      >
                        Services
                      </NavDropdown.Item>
                      {/* <NavDropdown.Item
                        onClick={() => navigate("/admin/servicestatuses")}
                      >
                        Service Statuses
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        onClick={() => navigate("/admin/modalities")}
                      >
                        Modalities
                      </NavDropdown.Item> */}
                    </NavDropdown>

                    <NavDropdown
                      className="HomesDropdown"
                      title="Homes"
                      id="homes-dropdown"
                    >
                      <NavDropdown.Item
                        onClick={() => navigate("/homes/homes")}
                      >
                        Homes
                      </NavDropdown.Item>
                      <NavDropdown.Item
                        onClick={() => navigate("/fosterhomes")}
                      >
                        SOAR Foster Homes
                      </NavDropdown.Item>
                      <NavDropdown.Item onClick={() => navigate("/pipeline")}>
                        Pipeline
                      </NavDropdown.Item>
                      {/* <NavDropdown.Item onClick={() => navigate("/apartments")}>
                        Apartments
                      </NavDropdown.Item> */}
                    </NavDropdown>
                  </>
                )}
                {process.env.REACT_APP_ENVIRONMENT !== "Production" &&
                (modules.includes("COMQI") || modules.includes("Admin")) ? (
                  <>
                    <NavLink
                      className="nav-item nav-link"
                      to="/requests"
                      id="requests"
                    >
                      Requests
                    </NavLink>
                  </>
                ) : null}
              </Nav>
              <Nav className="ms-auto">
                {advancedFindLink()}
                {timeLeft && timeLeft.difference > 0 ? (
                  <>
                    <div className="me-2">
                      <button
                        className={
                          timeLeft &&
                          (parseInt(timeLeft.hours) !== 0 ||
                            parseInt(timeLeft.minutes) > timeout - 1)
                            ? "btn btn-primary mt-3 pe-2"
                            : "btn btn-danger mt-3 p-2"
                        }
                        onClick={() => {
                          renewToken();
                        }}
                      >
                        {`${timeLeft.hours}:${timeLeft.minutes}:${timeLeft.seconds}`}
                      </button>
                    </div>
                  </>
                ) : null}
                <div className="mt-3 me-2">
                  <button
                    className="btn btn-primary"
                    onClick={() =>
                      window.open(
                        "https://safy365.sharepoint.com/sites/ClinicalInnovationTechnology/SitePages/SAFY-Portal-Resources.aspx"
                      )
                    }
                  >
                    Resources
                  </button>
                </div>
                <NavDropdown
                  className="profile"
                  title={
                    avatar ? (
                      <img
                        src={avatar}
                        alt="user"
                        className="rounded-circle align-self-center me-2"
                        style={{ width: "48px" }}
                      ></img>
                    ) : (
                      <i
                        className="far fa-user-circle fa-lg rounded-circle align-self-center me-2"
                        style={{ width: "48px" }}
                      ></i>
                    )
                  }
                  id="profile-nav-dropdown"
                  align="end"
                >
                  <NavDropdown.Item>
                    <h5 className="dropdown-item-text mb-0">
                      {instance.getActiveAccount()?.name}
                    </h5>
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item>
                    <p>{instance.getActiveAccount()?.username}</p>
                  </NavDropdown.Item>
                  {safyInfo && (
                    <NavDropdown.Item>
                      {safyInfo?.user?.jobTitle && (
                        <div>Title: {safyInfo.user.jobTitle}</div>
                      )}
                      {safyInfo?.user?.safyInfo?.BusinessUnitIdName && (
                        <div>
                          State: {safyInfo.user.safyInfo.BusinessUnitIdName}
                        </div>
                      )}
                      {safyInfo?.user?.officeLocation && (
                        <div>Location: {safyInfo.user.officeLocation}</div>
                      )}
                      {safyInfo?.user?.businessPhones && (
                        <div>Phone: {safyInfo.user.businessPhones}</div>
                      )}
                      {safyInfo?.user?.mobilePhone && (
                        <div>Mobile: {safyInfo.user.mobilePhone}</div>
                      )}
                    </NavDropdown.Item>
                  )}
                  <NavDropdown.Item>
                    Build:{process.env.REACT_APP_BUILD}
                  </NavDropdown.Item>
                  <NavDropdown.Item>React:{React.version}</NavDropdown.Item>
                  <NavDropdown.Item>
                    <div className="d-inline-block my-4">
                      <img
                        src={oktaIcon}
                        alt="okta logo"
                        style={{ width: "100px", height: "auto" }}
                        onClick={() => window.open("https://safy.okta.com")}
                        role="button"
                      ></img>
                      <div className="h5 d-inline-block">Sign In</div>
                    </div>
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item>
                    <Button
                      onClick={() =>
                        instance.logoutRedirect({
                          onRedirectNavigate: (url) => {
                            // Return false if you would like to stop navigation after local logout
                            navigate("/");
                            return false;
                          },
                        })
                      }
                    >
                      Sign Out
                    </Button>
                  </NavDropdown.Item>
                </NavDropdown>
              </Nav>
            </Navbar.Collapse>
          </Navbar>
        </>
      )}
      {!isAuthenticated &&
        inProgress !== InteractionStatus.Startup &&
        inProgress !== InteractionStatus.HandleRedirect && (
          <Navbar
            className={
              process.env.REACT_APP_ENVIRONMENT !== "Production"
                ? "bg-safy3-1 px-4"
                : "bg-safy1-1 px-4"
            }
            variant="dark"
          >
            <img
              src={"data:image/png " + logo}
              alt="logo"
              className="me-4"
              style={{ width: "48px" }}
            ></img>
            <Navbar.Brand href="/">{process.env.REACT_APP_NAME}</Navbar.Brand>
            <Navbar.Toggle aria-controls="navbarNavAltMarkupv" />
            <Navbar.Text className="w-100">
              <Button
                className="me-4 mt-3 float-end"
                onClick={() => {
                  renewToken().then(() => {
                    setTimeout(() => {
                      // setInfo();
                      navigate("/");
                    }, 3000);
                  });
                }}
              >
                Sign In
              </Button>
            </Navbar.Text>
          </Navbar>
        )}
    </>
  );
};

export default NavBar;
